import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Collapse,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select as MuiSelect,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import LoginIcon from "@mui/icons-material/Login";
import TodayIcon from "@mui/icons-material/Today";
import Helpers from "../Config/Helpers";
import PageShell from "./ModuleLayout/PageShell";
import AppointmentCustomerVehicleSelect from "./Appointments/AppointmentCustomerVehicleSelect";

const REASONS = ["Inspection", "Oil Change", "Brakes", "Tires", "Other"];
const STATUSES = [
  "Scheduled",
  "Confirmed",
  "Checked In",
  "Cancelled",
  "No Show",
  "Completed",
];

const pad = (value) => String(value).padStart(2, "0");

const dateInputValue = (date = new Date()) => {
  const value = new Date(date);
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
};

const dateTimeInputValue = (date = new Date()) => {
  const value = new Date(date);
  return `${dateInputValue(value)}T${pad(value.getHours())}:${pad(value.getMinutes())}`;
};

const startOfDayIso = (value) => new Date(`${value}T00:00:00`).toISOString();
const endOfDayIso = (value) => new Date(`${value}T23:59:59`).toISOString();

const nextAppointmentStart = () => {
  const date = new Date();
  date.setMinutes(date.getMinutes() > 30 ? 60 : 30, 0, 0);
  return date;
};

const createAppointmentForm = () => {
  const start = nextAppointmentStart();

  return {
    _id: "",
    customer: null,
    customer_id: "",
    vehicle: null,
    vehicle_id: "",
    startTime: dateTimeInputValue(start),
    reason: "Inspection",
    otherReason: "",
    notes: "",
    status: "Scheduled",
  };
};

const appointmentToForm = (appointment = {}) => ({
  _id: appointment._id || "",
  customer: appointment.customer || null,
  customer_id: appointment.customer_id || appointment.customer?._id || "",
  vehicle: appointment.vehicle || null,
  vehicle_id: appointment.vehicle_id || appointment.vehicle?._id || "",
  startTime: dateTimeInputValue(appointment.startTime),
  reason: appointment.reason || "Inspection",
  otherReason: appointment.otherReason || "",
  notes: appointment.notes || "",
  status: appointment.status || "Scheduled",
});

const formatDateTime = (value) =>
  new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));

const formatTime = (value) =>
  new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));

const formatShortDate = (value = "") => {
  const [, month, day] = String(value || "").split("-");
  if (!month || !day) return value;
  return `${Number(month)}/${Number(day)}`;
};

const formatVehicleLabel = (vehicle = {}) =>
  [vehicle.year, vehicle.make || vehicle.name, vehicle.model]
    .filter(Boolean)
    .join(" ") || "No vehicle selected";

const formatCustomerPhone = (value = "") => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return value || "";
};

const appointmentReason = (appointment = {}) =>
  appointment.reason === "Other" && appointment.otherReason
    ? appointment.otherReason
    : appointment.reason || "Appointment";

function Appointments() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [startDate, setStartDate] = useState(dateInputValue());
  const [endDate, setEndDate] = useState(dateInputValue());
  const [statusFilter, setStatusFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(createAppointmentForm);
  const [formError, setFormError] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const loadAppointments = useCallback(() => {
    setLoading(true);
    axios
      .get(`${Helpers.baseUrl}/appointments/all/${Helpers.authParentId}`, {
        ...Helpers.headers,
        params: {
          start: startOfDayIso(startDate),
          end: endOfDayIso(endDate),
          status: statusFilter,
          q: search.trim(),
        },
      })
      .then((response) => {
        setAppointments(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        Helpers.error_response(error);
        Helpers.unauthenticated(error, navigate);
      })
      .finally(() => setLoading(false));
  }, [endDate, navigate, search, startDate, statusFilter]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  const groupedAppointments = useMemo(() => {
    return appointments.reduce((groups, appointment) => {
      const key = dateInputValue(appointment.startTime);
      if (!groups[key]) groups[key] = [];
      groups[key].push(appointment);
      return groups;
    }, {});
  }, [appointments]);

  const counts = useMemo(
    () =>
      appointments.reduce(
        (total, appointment) => {
          total[appointment.status] = (total[appointment.status] || 0) + 1;
          total.all += 1;
          return total;
        },
        { all: 0 },
      ),
    [appointments],
  );

  const filterSummary = useMemo(() => {
    const today = dateInputValue();
    const isTodayView =
      startDate === today &&
      endDate === today &&
      statusFilter === "All" &&
      !search.trim();

    if (isTodayView) return "Today";

    const parts = [
      startDate === endDate
        ? formatShortDate(startDate)
        : `${formatShortDate(startDate)} - ${formatShortDate(endDate)}`,
    ];
    if (statusFilter !== "All") parts.push(statusFilter);
    if (search.trim()) parts.push(`Search: ${search.trim()}`);
    return parts.filter(Boolean).join(" / ");
  }, [endDate, search, startDate, statusFilter]);

  const openNewAppointment = () => {
    const nextForm = createAppointmentForm();
    setEditing(false);
    setForm(nextForm);
    setFormError("");
    setDialogOpen(true);
  };

  const openEditAppointment = (appointment) => {
    const nextForm = appointmentToForm(appointment);
    setEditing(true);
    setForm(nextForm);
    setFormError("");
    setDialogOpen(true);
  };

  const handleCustomerChange = (customer) => {
    setForm((current) => ({
      ...current,
      customer,
      customer_id: customer?._id || "",
      vehicle: null,
      vehicle_id: "",
    }));
  };

  const handleVehicleChange = (vehicle) => {
    const vehicleCustomer =
      vehicle?.customer && typeof vehicle.customer === "object"
        ? vehicle.customer
        : null;

    setForm((current) => ({
      ...current,
      customer: current.customer || vehicleCustomer || null,
      customer_id:
        current.customer?._id || current.customer_id || vehicleCustomer?._id || "",
      vehicle,
      vehicle_id: vehicle?._id || "",
    }));
  };

  const buildPayload = () => ({
    ...form,
    customer_id: form.customer?._id || form.customer_id,
    vehicle_id: form.vehicle?._id || form.vehicle_id || "",
    startTime: new Date(form.startTime).toISOString(),
  });

  const validateForm = () => {
    if (!form.customer && !form.customer_id) return "Select a customer.";
    if (!form.startTime) return "Appointment time is required.";
    if (Number.isNaN(new Date(form.startTime).getTime())) {
      return "Enter a valid appointment time.";
    }
    return "";
  };

  const saveAppointment = () => {
    const error = validateForm();
    if (error) {
      setFormError(error);
      return;
    }

    setSaving(true);
    const url = editing
      ? `${Helpers.baseUrl}/appointments/update`
      : `${Helpers.baseUrl}/appointments/add`;

    axios
      .post(url, buildPayload(), Helpers.headers)
      .then(() => {
        Helpers.toast("success", editing ? "Appointment updated" : "Appointment saved");
        setDialogOpen(false);
        loadAppointments();
      })
      .catch((errorResponse) => {
        setFormError(
          errorResponse?.response?.data?.error?.message ||
            errorResponse?.response?.data?.error ||
            "Could not save appointment.",
        );
        Helpers.error_response(errorResponse);
        Helpers.unauthenticated(errorResponse, navigate);
      })
      .finally(() => setSaving(false));
  };

  const updateStatus = (appointment, status) => {
    axios
      .patch(
        `${Helpers.baseUrl}/appointments/status/${appointment._id}`,
        { status },
        Helpers.headers,
      )
      .then((response) => {
        setAppointments((current) =>
          current.map((item) =>
            item._id === appointment._id ? response.data : item,
          ),
        );
        Helpers.toast("success", `Appointment marked ${status}`);
      })
      .catch((error) => {
        Helpers.error_response(error);
        Helpers.unauthenticated(error, navigate);
      });
  };

  const deleteAppointment = (appointment) => {
    if (!window.confirm("Delete this appointment?")) return;

    axios
      .get(`${Helpers.baseUrl}/appointments/delete/${appointment._id}`, Helpers.headers)
      .then(() => {
        setAppointments((current) =>
          current.filter((item) => item._id !== appointment._id),
        );
        Helpers.toast("success", "Appointment deleted");
      })
      .catch((error) => {
        Helpers.error_response(error);
        Helpers.unauthenticated(error, navigate);
      });
  };

  const setToday = () => {
    const today = dateInputValue();
    setStartDate(today);
    setEndDate(today);
    if (isMobile) setFiltersOpen(false);
  };

  const checkInAppointment = (appointment) => {
    navigate("/checkin", { state: { appointment } });
  };

  return (
    <PageShell
      title="Appointments"
      description={`${counts.all} scheduled in selected view`}
      icon={<CalendarMonthIcon sx={{ color: "white", fontSize: 34 }} />}
      actions={
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={openNewAppointment}
          sx={{
            minHeight: 42,
            width: { xs: "100%", sm: "auto" },
            bgcolor: "#ffffff",
            borderColor: "rgba(255,255,255,0.75)",
            color: "#5a4fcf",
            fontWeight: 700,
            "&:hover": {
              bgcolor: "#f7f7ff",
              borderColor: "#ffffff",
            },
          }}
        >
          New Appointment
        </Button>
      }
    >

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 2 }}>
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          justifyContent="space-between"
          sx={{ display: { xs: "flex", sm: "none" } }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              View
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {filterSummary}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: filtersOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              />
            }
            onClick={() => setFiltersOpen((current) => !current)}
            sx={{ flexShrink: 0 }}
          >
            Filters
          </Button>
        </Stack>

        <Collapse in={!isMobile || filtersOpen} timeout="auto">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                md: "repeat(12, minmax(0, 1fr))",
              },
              gap: { xs: 1.5, sm: 2 },
              alignItems: "center",
              mt: { xs: 1.5, sm: 0 },
            }}
          >
            <Box sx={{ gridColumn: { xs: "span 2", sm: "span 1", md: "span 2" } }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                size={isMobile ? "small" : "medium"}
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ gridColumn: { xs: "span 2", sm: "span 1", md: "span 2" } }}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                size={isMobile ? "small" : "medium"}
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ gridColumn: { xs: "span 2", sm: "span 1", md: "span 2" } }}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel>Status</InputLabel>
                <MuiSelect
                  label="Status"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <MenuItem value="All">All</MenuItem>
                  {STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>
            </Box>
            <Box sx={{ gridColumn: { xs: "span 2", md: "span 4" } }}>
              <TextField
                fullWidth
                label="Search"
                size={isMobile ? "small" : "medium"}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Customer, vehicle, reason"
              />
            </Box>
            <Box sx={{ gridColumn: { xs: "span 1", md: "span 1" } }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<TodayIcon />}
                onClick={setToday}
                sx={{ minHeight: 42 }}
              >
                Today
              </Button>
            </Box>
            <Box sx={{ gridColumn: { xs: "span 1", md: "span 1" } }}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  loadAppointments();
                  if (isMobile) setFiltersOpen(false);
                }}
                sx={{ minHeight: 42 }}
              >
                Refresh
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Paper>

      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        sx={{ mb: 2 }}
      >
        <Chip size="small" label={`Scheduled ${counts.Scheduled || 0}`} />
        <Chip size="small" label={`Confirmed ${counts.Confirmed || 0}`} color="primary" />
        <Chip size="small" label={`Checked In ${counts["Checked In"] || 0}`} color="success" />
        <Chip size="small" label={`Completed ${counts.Completed || 0}`} color="default" />
        <Chip size="small" label={`Cancelled ${counts.Cancelled || 0}`} color="warning" />
      </Stack>

      {loading ? (
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
          <CircularProgress size={28} />
        </Paper>
      ) : Object.keys(groupedAppointments).length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
          <EventAvailableIcon sx={{ color: "#98a2b3", fontSize: 42, mb: 1 }} />
          <Typography color="text.secondary">No appointments in this view.</Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {Object.entries(groupedAppointments).map(([date, dayAppointments]) => (
            <Paper key={date} sx={{ p: { xs: 1.25, sm: 2 }, borderRadius: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 1.5,
                  fontWeight: 700,
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                }}
              >
                {new Intl.DateTimeFormat(undefined, {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                }).format(new Date(`${date}T12:00:00`))}
              </Typography>
              <Stack spacing={1.25}>
                {dayAppointments.map((appointment) => (
                  <Paper
                    key={appointment._id}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      overflow: "hidden",
                      borderColor:
                        appointment.status === "Cancelled" ? "#f6c7c7" : "#e4e7ec",
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={1.5}
                      justifyContent="space-between"
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography sx={{ fontWeight: 700 }}>
                            {formatTime(appointment.startTime)}
                          </Typography>
                          <Chip size="small" label={appointment.status} />
                          <Chip size="small" variant="outlined" label={appointmentReason(appointment)} />
                          {appointment.googleSyncStatus === "Error" ? (
                            <Chip size="small" color="error" label="Calendar sync failed" />
                          ) : null}
                        </Stack>
                        <Typography
                          sx={{
                            mt: 0.75,
                            fontWeight: 600,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {appointment.customer?.name || "Customer"}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ overflowWrap: "anywhere" }}
                        >
                          {[formatCustomerPhone(appointment.customer?.phone), appointment.customer?.email]
                            .filter(Boolean)
                            .join(" · ") || "No contact info"}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ overflowWrap: "anywhere" }}
                        >
                          {formatVehicleLabel(appointment.vehicle || {})}
                          {appointment.vehicle?.vin_number ? ` · ${appointment.vehicle.vin_number}` : ""}
                        </Typography>
                        {appointment.notes ? (
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {appointment.notes}
                          </Typography>
                        ) : null}
                      </Box>

                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        justifyContent={{ xs: "flex-start", md: "flex-end" }}
                        flexWrap="wrap"
                        sx={{
                          width: { xs: "100%", md: "auto" },
                          "& .MuiButton-root": {
                            flex: { xs: "1 1 100%", sm: "0 0 auto" },
                          },
                          "& .MuiIconButton-root": {
                            border: "1px solid #e4e7ec",
                          },
                        }}
                      >
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<LoginIcon />}
                          onClick={() => checkInAppointment(appointment)}
                          disabled={appointment.status === "Cancelled"}
                        >
                          Check In
                        </Button>
                        {appointment.status !== "Confirmed" ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => updateStatus(appointment, "Confirmed")}
                            disabled={appointment.status === "Cancelled"}
                          >
                            Confirm
                          </Button>
                        ) : null}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => updateStatus(appointment, "Completed")}
                          disabled={appointment.status === "Cancelled"}
                        >
                          Complete
                        </Button>
                        <IconButton
                          color="success"
                          size="small"
                          onClick={() => openEditAppointment(appointment)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() =>
                            appointment.status === "Cancelled"
                              ? deleteAppointment(appointment)
                              : updateStatus(appointment, "Cancelled")
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            m: { xs: 0, sm: 4 },
            maxHeight: { xs: "100%", sm: "calc(100% - 64px)" },
          },
        }}
      >
        <DialogTitle sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
          {editing ? "Edit Appointment" : "New Appointment"}
        </DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            {formError ? (
              <Paper sx={{ p: 1.5, bgcolor: "#fff1f3", color: "#b42318" }}>
                {formError}
              </Paper>
            ) : null}

            <AppointmentCustomerVehicleSelect
              customer={form.customer}
              vehicle={form.vehicle}
              onCustomerChange={handleCustomerChange}
              onVehicleChange={handleVehicleChange}
              onUnauthenticated={(error) => Helpers.unauthenticated(error, navigate)}
            />

            <TextField
              fullWidth
              label="Appointment Time"
              type="datetime-local"
              size={isMobile ? "small" : "medium"}
              value={form.startTime}
              onChange={(event) =>
                setForm((current) => ({ ...current, startTime: event.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                  <InputLabel>Reason</InputLabel>
                  <MuiSelect
                    label="Reason"
                    value={form.reason}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, reason: event.target.value }))
                    }
                  >
                    {REASONS.map((reason) => (
                      <MenuItem key={reason} value={reason}>
                        {reason}
                      </MenuItem>
                    ))}
                  </MuiSelect>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                  <InputLabel>Status</InputLabel>
                  <MuiSelect
                    label="Status"
                    value={form.status}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, status: event.target.value }))
                    }
                  >
                    {STATUSES.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </MuiSelect>
                </FormControl>
              </Grid>
            </Grid>

            {form.reason === "Other" ? (
              <TextField
                fullWidth
                label="Other Reason"
                size={isMobile ? "small" : "medium"}
                value={form.otherReason}
                onChange={(event) =>
                  setForm((current) => ({ ...current, otherReason: event.target.value }))
                }
              />
            ) : null}

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Notes"
              size={isMobile ? "small" : "medium"}
              value={form.notes}
              onChange={(event) =>
                setForm((current) => ({ ...current, notes: event.target.value }))
              }
            />

            {form.startTime ? (
              <Typography variant="body2" color="text.secondary">
                {formatDateTime(form.startTime)}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: { xs: 1, sm: 0 },
            "& > :not(style) ~ :not(style)": {
              ml: { xs: 0, sm: 1 },
            },
          }}
        >
          <Button
            onClick={() => setDialogOpen(false)}
            fullWidth={isMobile}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveAppointment}
            disabled={saving}
            fullWidth={isMobile}
          >
            {saving ? "Saving..." : "Save Appointment"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}

export default Appointments;
